import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  MessageCircle, Trash2, Send, Loader2, Pencil, X, MoreHorizontal, Flag, Repeat2, Check, PenSquare,
  Sparkles, Globe, ChevronDown, ChevronUp, ArrowDown, ArrowUp, AtSign, Pin, Megaphone,
  ShieldAlert,
  MessageSquare,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReportDialog } from "@/components/social/ReportDialog";
import { BlockMenuItem } from "@/components/social/BlockMenuItem";
import api, { fileUrl } from "@/lib/api";
import { RichTextEditor, RichTextView } from "@/components/forms/RichTextEditor";
import { PostMediaView, ChatterComposerMedia, EMPTY_MEDIA, hasMedia } from "@/components/social/PostMedia";
import { LinkPreviewCard, useLinkPreview } from "@/components/social/LinkPreviewCard";
import { ClampedBody } from "@/components/social/ClampedBody";
import { ReactionBar } from "@/components/social/ReactionBar";
import { ReactionCountButton } from "@/components/social/reactions/ReactionCountButton";
import { EmptyState } from "@/components/common/Loading";
import { timeAgo } from "@/lib/timeago";
import { MentionInput } from "@/components/social/MentionAutocomplete";
import { EmojiPickerButton } from "@/components/social/EmojiPickerButton";
import { OtterAssistComposerPanel, OtterAssistPostPill, useOtterAssist } from "@/components/social/OtterAssistComposerPanel";
import { OtterAssistActivationDialog } from "@/components/social/OtterAssistActivationDialog";
import { OtterAssistEditDialog } from "@/components/social/OtterAssistEditDialog";
import { renderTextWithMentions } from "@/lib/mentions";
import { AuthorLink, AuthorAvatar, profileHref } from "@/components/social/AuthorLink";
import { MultiSelect } from "@/components/forms/MultiSelect";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

// Chatter post bodies cap at 3,000 visible characters (HTML-stripped). Server
// enforces the same — see chatter.py::CHATTER_POST_MAX_PLAIN.
const POST_MAX = 3000;
const LABEL_MAX = 3;

function PostTypeChips({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap justify-start gap-1.5" data-testid="post-type-chips">
      {items.map((t) => (
        <span key={t.id} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-[560]" style={{ borderColor: t.color, color: t.color }} data-testid={`post-type-chip-${t.id}`}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: t.color }} />
          {t.name}
        </span>
      ))}
    </div>
  );
}

function IndustryChips({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="industry-chips">
      {items.map((i) => (
        <span key={i.value} className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-[540] text-muted-foreground" data-testid={`industry-chip-${i.value}`}>{i.label}</span>
      ))}
    </div>
  );
}

function AffiliationLine({ aff }) {
  if (!aff || !aff.role) return null;
  const company = aff.company_name;
  if (!company) return <span data-testid="post-affiliation">{aff.role}</span>;
  const href = aff.visible && aff.slug ? profileHref({ slug: aff.slug, target_type: aff.target_type }) : null;
  return (
    <span data-testid="post-affiliation">
      {aff.role} at {href
        ? <Link to={href} onClick={(e) => e.stopPropagation()} className="font-[540] text-muted-foreground hover:text-primary hover:underline" data-testid="post-affiliation-company">{company}</Link>
        : <span className="font-[540]">{company}</span>}
    </span>
  );
}


export function LabelSelectors({ postTypeOpts, industryOpts, postTypeIds, setPostTypeIds, industryIds, setIndustryIds, idSuffix, compact = false }) {
  // `compact` is the Stream presentation (GRP-DEC-320): the SAME two selectors, inline on
  // the composer's primary action row instead of a two-column labelled block.
  if (compact) {
    return (
      <>
        <div className="w-[9.5rem]">
          <MultiSelect options={postTypeOpts} value={postTypeIds} onChange={setPostTypeIds} max={LABEL_MAX} placeholder="Post type" testId={`chatter-post-types${idSuffix}`} />
        </div>
        <div className="w-[9.5rem]">
          <MultiSelect options={industryOpts} value={industryIds} onChange={setIndustryIds} max={LABEL_MAX} placeholder="Industry" testId={`chatter-industries${idSuffix}`} />
        </div>
      </>
    );
  }
  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-2">
      <div>
        <label className="mb-1.5 block text-xs font-[560] text-muted-foreground">Post types <span className="text-muted-foreground/70">(up to {LABEL_MAX})</span></label>
        <MultiSelect options={postTypeOpts} value={postTypeIds} onChange={setPostTypeIds} max={LABEL_MAX} placeholder="Add post types…" testId={`chatter-post-types${idSuffix}`} />
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-[560] text-muted-foreground">Industries <span className="text-muted-foreground/70">(up to {LABEL_MAX})</span></label>
        <MultiSelect options={industryOpts} value={industryIds} onChange={setIndustryIds} max={LABEL_MAX} placeholder="Add industries…" testId={`chatter-industries${idSuffix}`} />
      </div>
    </div>
  );
}
// One class for every post action-row button. Phones get tighter padding and a tighter
// icon-to-label gap so React · Comments · Re-Chat · Share · Message fit on ONE line;
// from `sm` up the spacing is exactly what it was.
const ACTION_BTN = "h-8 gap-1 px-1.5 text-xs sm:h-9 sm:gap-1.5 sm:px-3 sm:text-sm";
const ACTION_ICON = "h-4 w-4";

const PLAIN = (html) =>
  (html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .trim().length;

function Comments({ postId, onCountChange, targetComment, commentIdentity = null }) {
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  // Groups Phase 5 (GRP-DEC-126) — an Owner/Admin may answer AS the Group.
  const [asGroup, setAsGroup] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try { const { data } = await api.get(`/chatter/posts/${postId}/comments`); if (active) setItems(data); }
      catch (e) { if (active) setItems([]); }
    })();
    return () => { active = false; };
  }, [postId]);

  useEffect(() => {
    if (!targetComment || !items || !items.some((c) => c.id === targetComment)) return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById(`comment-${targetComment}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("so-flash");
        setTimeout(() => el.classList.remove("so-flash"), 2900);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [targetComment, items]);

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/chatter/posts/${postId}/comments`, {
        body: text.trim(),
        ...(asGroup && commentIdentity
          ? { identity_type: "group", identity_id: commentIdentity.identity_id }
          : {}) });
      setItems((prev) => [data, ...(prev || [])]);
      setText("");
      if (onCountChange) onCountChange(1);
    } catch (e) { toast.error("Could not comment"); }
    finally { setBusy(false); }
  };

  const update = (data) => setItems((prev) => (prev || []).map((c) => (c.id === data.id ? data : c)));
  const remove = async (id) => {
    try {
      await api.delete(`/chatter/comments/${id}`);
      setItems((prev) => prev.filter((c) => c.id !== id));
      if (onCountChange) onCountChange(-1);
    } catch (e) { toast.error("Could not delete"); }
  };
  const hide = (id) => {
    setItems((prev) => prev.filter((c) => c.id !== id));
    if (onCountChange) onCountChange(-1);
  };

  return (
    // No top rule here: a divider made comments read as a separate block rather than
    // part of the post. Post-to-post separators are untouched.
    <div className="mt-3 rounded-lg bg-[#ecf1f1] px-4 py-3" data-testid={`comments-${postId}`}>
      <div className="divide-y divide-border/60">
        {(items || []).map((c) => (
          <CommentRow key={c.id} c={c} onChanged={update} onRemove={() => remove(c.id)} onReported={() => hide(c.id)} />
        ))}
      </div>
      {commentIdentity && (
        <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs text-muted-foreground"
               data-testid={`comment-as-group-toggle-${postId}`}>
          <input type="checkbox" checked={asGroup} className="h-3.5 w-3.5 accent-teal-600"
                 onChange={(e) => setAsGroup(e.target.checked)} />
          Reply as {commentIdentity.display_name}
        </label>
      )}
      <div className="mt-3 flex items-center gap-2">
        <EmojiPickerButton targetRef={inputRef} value={text} onChange={(v) => setText(v.slice(0, 1000))} testId={`comment-emoji-${postId}`} />
        <MentionInput
          inputRef={inputRef}
          value={text}
          onChange={(v) => setText(v.slice(0, 1000))}
          maxLength={1000}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Write a comment…"
          testId={`comment-input-${postId}`}
        />
        <Button size="sm" onClick={add} disabled={busy || !text.trim()} data-testid={`comment-submit-${postId}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

function CommentRow({ c, onChanged, onRemove, onReported }) {
  const reactionTarget = useMemo(() => ({ type: "comment", id: c.id }), [c.id]);
  const [reactorEmoji, setReactorEmoji] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(c.body);
  const [saving, setSaving] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const canReport = !c.can_edit && !c.can_delete;

  const react = async (emoji) => {
    try { const { data } = await api.post(`/chatter/comments/${c.id}/react`, { emoji }); onChanged(data); }
    catch (e) { toast.error("Could not react"); }
  };
  const saveEdit = async () => {
    if (!editText.trim()) return;
    setSaving(true);
    try { const { data } = await api.put(`/chatter/comments/${c.id}`, { body: editText.trim() }); onChanged(data); setEditing(false); }
    catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const a = c.author || {};
  return (
    <div id={`comment-${c.id}`} className="py-3" data-testid={`comment-${c.id}`}>
      {/* Row 1: avatar + name + time */}
      <div className="flex items-center gap-2.5">
        <AuthorAvatar card={a}>
          <Avatar className="h-7 w-7">
            {a.headshot_url && <AvatarImage src={fileUrl(a.headshot_url)} alt="" />}
            <AvatarFallback className="bg-secondary text-[11px]">{(a.display_name || "?")[0]}</AvatarFallback>
          </Avatar>
        </AuthorAvatar>
        <AuthorLink card={a} className="text-sm font-[560] text-foreground" testId={`comment-author-${c.id}`} />
        {c.posted_as_group && (
          <span className="rounded bg-teal-50 px-1.5 py-0.5 text-[10px] font-[640] uppercase tracking-wide text-teal-700"
                data-testid={`comment-group-badge-${c.id}`}>Group</span>
        )}
        <span className="text-xs text-muted-foreground">{timeAgo(c.created_at)}{c.edited ? " · edited" : ""}</span>
      </div>

      {/* Row 2: comment body, full width */}
      {editing ? (
        <div className="mt-2">
          <MentionInput value={editText} onChange={(v) => setEditText(v.slice(0, 1000))} maxLength={1000} onKeyDown={(e) => e.key === "Enter" && saveEdit()} testId={`comment-edit-input-${c.id}`} knownHandles={(c.mentions || []).map((m) => m.handle_norm || m.handle)} />
          <div className="mt-1.5 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setEditText(c.body); }}>Cancel</Button>
            <Button size="sm" onClick={saveEdit} disabled={saving || !editText.trim()} data-testid={`comment-edit-save-${c.id}`}>Save</Button>
          </div>
        </div>
      ) : (
        <p className="mt-1.5 break-words text-sm leading-[1.4] text-foreground">{renderTextWithMentions(c.body, c.mentions)}</p>
      )}

      {!editing && (
        <div className="mt-1.5 flex items-center gap-3">
          <ReactionBar reactions={c.reactions} myReaction={c.my_reaction} onReact={react} testId={`comment-react-${c.id}`}
            target={reactionTarget} onSeeReactors={setReactorEmoji} />
          <ReactionCountButton reactions={c.reactions} target={reactionTarget}
            testId={`comment-reaction-count-${c.id}`} openEmoji={reactorEmoji} onOpenChange={setReactorEmoji} />
          {c.can_edit && <button onClick={() => setEditing(true)} className="text-xs font-[540] text-muted-foreground hover:text-primary" data-testid={`comment-edit-${c.id}`}>Edit</button>}
          {c.can_delete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="text-xs font-[540] text-muted-foreground hover:text-destructive" data-testid={`comment-delete-${c.id}`}>Delete</button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Delete this comment?</AlertDialogTitle><AlertDialogDescription>This can&apos;t be undone.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={onRemove} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {canReport && (
            <button onClick={() => setReportOpen(true)} className="text-xs font-[540] text-muted-foreground hover:text-destructive" data-testid={`comment-report-${c.id}`}>Report</button>
          )}
        </div>
      )}
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} contentType="comment" contentId={c.id} onReported={onReported} />
    </div>
  );
}

export const LAST_IDENTITY_KEY = "so_last_chatter_identity";
export const identityKeyOf = (i) => `${i.identity_type}:${i.identity_id}`;

/** Read-only embedded original inside a Re-Chat (or an unavailable placeholder). */
export function RechatEmbed({ original }) {
  if (!original) return null;
  if (original.unavailable) {
    return (
      <div className="mt-3 rounded-xl border border-dashed border-border bg-secondary/30 px-4 py-3 text-sm text-muted-foreground" data-testid="rechat-unavailable">
        This post is no longer available.
      </div>
    );
  }
  const a = original.author || {};
  return (
    <div className="mt-3 rounded-xl border border-border bg-secondary/20 p-4" data-testid={`rechat-embed-${original.id}`}>
      <div className="flex items-center gap-2.5">
        <AuthorAvatar card={a}>
          <Avatar className="h-8 w-8">
            {a.headshot_url && <AvatarImage src={fileUrl(a.headshot_url)} alt="" />}
            <AvatarFallback className="bg-teal-50 text-xs text-teal-700">{(a.display_name || "?")[0]}</AvatarFallback>
          </Avatar>
        </AuthorAvatar>
        <div className="min-w-0">
          <AuthorLink card={a} className="block text-sm font-[600] text-foreground" testId={`rechat-embed-author-${original.id}`} />
          <div className="text-xs text-muted-foreground">{timeAgo(original.created_at)}{original.edited ? " · edited" : ""}</div>
        </div>
      </div>
      {original.post_types && original.post_types.length > 0 && <div className="mt-2"><PostTypeChips items={original.post_types} /></div>}
      {original.body && original.body !== "<p></p>" && <div className="mt-2"><RichTextView html={original.body} mentions={original.mentions} className="leading-[1.35]" /></div>}
      <PostMediaView media={original.media} />
      <IndustryChips items={original.industries} />
    </div>
  );
}

/** "Re-Chat with your thoughts" composer — a quote Re-Chat behaves like a normal post. */
function RechatQuoteDialog({ open, onOpenChange, post, identities, defaultIdentityKey, postTypeOpts, industryOpts, onPosted, groupId, streamId }) {
  const firstKey = identities[0] ? identityKeyOf(identities[0]) : "";
  const [identityKey, setIdentityKey] = useState(defaultIdentityKey || firstKey);
  const [body, setBody] = useState("");
  const [media, setMedia] = useState(EMPTY_MEDIA);
  const [ptIds, setPtIds] = useState([]);
  const [indIds, setIndIds] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIdentityKey(defaultIdentityKey || firstKey);
    setBody(""); setMedia(EMPTY_MEDIA); setPtIds([]); setIndIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The Charla bubble floats over the dialog's buttons on phones — park it while open.
  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add("hide-charla-mobile");
    return () => document.body.classList.remove("hide-charla-mobile");
  }, [open]);

  const previewOriginal = post.repost_of && post.original ? post.original : {
    id: post.id, author: post.author, body: post.body, media: post.media,
    post_types: post.post_types, industries: post.industries,
    created_at: post.created_at, edited: post.edited, mentions: post.mentions,
  };

  const empty = !body || body === "<p></p>";
  const over = PLAIN(body) > POST_MAX;
  const canPost = (!empty || hasMedia(media)) && !over && !!identityKey;

  const submit = async () => {
    if (!canPost) return;
    const [type, id] = identityKey.split(":");
    setBusy(true);
    try {
      const { data } = await api.post(`/chatter/posts/${post.id}/rechat`, {
        identity_type: type, identity_id: id, kind: "quote_rechat",
        ...(groupId ? { group_id: groupId, ...(streamId ? { stream_id: streamId } : {}) } : {}),
        body: empty ? "" : body, media: hasMedia(media) ? media : null,
        post_type_ids: ptIds, industry_ids: indIds,
      });
      try { localStorage.setItem(LAST_IDENTITY_KEY, identityKey); } catch (e) { /* ignore */ }
      onPosted && onPosted(data);
      toast.success("Re-Chatted with your thoughts");
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not Re-Chat"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88dvh] max-w-lg overflow-y-auto" data-testid={`rechat-quote-dialog-${post.id}`}>
        <DialogHeader><DialogTitle>Re-Chat with your thoughts</DialogTitle></DialogHeader>
        {identities.length > 1 && (
          <div>
            <label className="mb-1.5 block text-xs font-[560] text-muted-foreground">Posting as</label>
            <Select value={identityKey} onValueChange={setIdentityKey}>
              <SelectTrigger data-testid={`rechat-quote-identity-${post.id}`}><SelectValue placeholder="Choose an identity" /></SelectTrigger>
              <SelectContent>
                {identities.map((i) => (
                  <SelectItem key={identityKeyOf(i)} value={identityKeyOf(i)} data-testid={`rechat-quote-identity-option-${i.identity_id}`}>
                    {i.display_name} · <span className="capitalize">{i.identity_type}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <RichTextEditor value={body} onChange={setBody} placeholder="Add your thoughts…" testId={`rechat-quote-input-${post.id}`} mentions emoji
                        onSubmit={() => { if (!busy && canPost) submit(); }} />
        <ChatterComposerMedia value={media} onChange={setMedia} />
        <LabelSelectors
          postTypeOpts={postTypeOpts} industryOpts={industryOpts}
          postTypeIds={ptIds} setPostTypeIds={setPtIds}
          industryIds={indIds} setIndustryIds={setIndIds}
          idSuffix={`-rechat-${post.id}`}
        />
        <RechatEmbed original={previewOriginal} />
        <DialogFooter className="mt-2 flex items-center justify-between gap-3 sm:justify-between">
          <span className={`text-xs tabular-nums ${over ? "font-[600] text-destructive" : "text-muted-foreground"}`} data-testid={`rechat-quote-count-${post.id}`}>{PLAIN(body).toLocaleString()}/3,000</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !canPost} data-testid={`rechat-quote-submit-${post.id}`}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Re-Chat
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Re-Chat action: per-identity instant toggle + "with your thoughts" quote composer. */
function RechatButton({ p, setP, identities, defaultIdentityKey, postTypeOpts, industryOpts, onRechatPosted, onRechatRemoved, groupId, streamId }) {
  // Groups Phase 5 (GRP-DEC-271): inside a Group, a Re-Chat lands in that same Group AND
  // in the Stream the reader is actually in — stating the group without the stream used to
  // silently route it to the Group's default Stream, which is why it never showed up in
  // the feed it was made from (correction §5). The server is still the authority: it
  // refuses any destination the root's own distribution doesn't allow.
  const [quoteOpen, setQuoteOpen] = useState(false);
  const myKeys = p.my_rechat || [];
  const dest = groupId
    ? { group_id: groupId, ...(streamId ? { stream_id: streamId } : {}) } : {};

  const toggleIdentity = async (idKey) => {
    const [type, id] = idKey.split(":");
    const has = myKeys.includes(idKey);
    try {
      if (has) {
        const { data } = await api.delete(`/chatter/posts/${p.id}/rechat`, {
          params: { identity_type: type, identity_id: id, ...(groupId ? { group_id: groupId } : {}) },
        });
        setP((prev) => ({ ...prev, my_rechat: (prev.my_rechat || []).filter((k) => k !== idKey), repost_count: Math.max(0, (prev.repost_count || 0) - 1) }));
        if (onRechatRemoved && data?.removed_id) onRechatRemoved(data.removed_id);
        toast.success("Re-Chat removed");
      } else {
        const { data } = await api.post(`/chatter/posts/${p.id}/rechat`, {
          identity_type: type, identity_id: id, kind: "rechat", ...dest });
        try { localStorage.setItem(LAST_IDENTITY_KEY, idKey); } catch (e) { /* ignore */ }
        setP((prev) => ({ ...prev, my_rechat: [...(prev.my_rechat || []), idKey], repost_count: (prev.repost_count || 0) + 1 }));
        // The Re-Chat is a post of its own, so it belongs in the feed it was made from —
        // no refresh required (correction §5).
        if (onRechatPosted) onRechatPosted(data);
        toast.success("Re-Chatted");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not Re-Chat"); }
  };

  const active = myKeys.length > 0;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className={cn(ACTION_BTN, active && "text-primary")}
                  data-testid={`rechat-button-${p.id}`}>
            <Repeat2 className={ACTION_ICON} />
            {p.repost_count > 0 ? `${p.repost_count} ` : ""}Re-Chat
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-60">
          {identities.map((i) => {
            const key = identityKeyOf(i);
            const has = myKeys.includes(key);
            return (
              <DropdownMenuItem key={key} onClick={() => toggleIdentity(key)} data-testid={`rechat-as-${i.identity_id}`}>
                <Repeat2 className="mr-2 h-4 w-4" />
                <span className="truncate">{has ? "Undo Re-Chat" : "Re-Chat"} as {i.display_name}</span>
                {has && <Check className="ml-auto h-4 w-4 text-primary" />}
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setQuoteOpen(true)} data-testid={`rechat-with-thoughts-${p.id}`}>
            <PenSquare className="mr-2 h-4 w-4" /> Re-Chat with your thoughts…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <RechatQuoteDialog
        open={quoteOpen} onOpenChange={setQuoteOpen} post={p} identities={identities}
        groupId={groupId} streamId={streamId}
        defaultIdentityKey={defaultIdentityKey} postTypeOpts={postTypeOpts} industryOpts={industryOpts}
        onPosted={(np) => { if (onRechatPosted) onRechatPosted(np); setP((prev) => ({ ...prev, repost_count: (prev.repost_count || 0) + 1 })); }}
      />
    </>
  );
}

export function PostCard({ post, onDeleted, autoOpen, targetComment, postTypeOpts = [], industryOpts = [],
  identities = [], rechatVisible = false, defaultIdentityKey = null, onRechatPosted, onRechatRemoved,
  commentIdentity = null, groupId = null, streamId = null,
  groupCanManage = false, onPinChanged, onMessageAuthor = null, viewerUserId = null }) {
  const [p, setP] = useState(post);
  const reactionTarget = useMemo(() => ({ type: "post", id: post.id }), [post.id]);
  const [reactorEmoji, setReactorEmoji] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(post.comment_count || 0);
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(post.body || "");
  const [editMedia, setEditMedia] = useState(post.media || EMPTY_MEDIA);
  const [editPostTypeIds, setEditPostTypeIds] = useState((post.post_types || []).map((x) => x.id));
  const [editIndustryIds, setEditIndustryIds] = useState((post.industries || []).map((x) => x.value));
  const [saving, setSaving] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [assistEditOpen, setAssistEditOpen] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!autoOpen) return undefined;
    const timers = [];
    if (targetComment) timers.push(setTimeout(() => setShowComments(true), 0));
    timers.push(setTimeout(() => {
      const el = cardRef.current;
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("so-flash"); }
    }, 200));
    timers.push(setTimeout(() => cardRef.current && cardRef.current.classList.remove("so-flash"), 3100));
    return () => timers.forEach(clearTimeout);
  }, [autoOpen, targetComment]);

  const react = async (emoji) => {
    try { const { data } = await api.post(`/chatter/posts/${p.id}/react`, { emoji }); setP(data); }
    catch (e) { toast.error("Could not react"); }
  };
  const del = async () => {
    try { await api.delete(`/chatter/posts/${p.id}`); if (onDeleted) onDeleted(p.id); toast.success("Post deleted"); }
    catch (e) { toast.error("Could not delete"); }
  };

  const startEdit = () => {
    setEditBody(p.body && p.body !== "<p></p>" ? p.body : "");
    setEditMedia(p.media || EMPTY_MEDIA);
    setEditPostTypeIds((p.post_types || []).map((x) => x.id));
    setEditIndustryIds((p.industries || []).map((x) => x.value));
    setEditing(true);
  };
  const emptyEdit = !editBody || editBody === "<p></p>";
  const editLink = useLinkPreview(editBody, { enabled: editing && !hasMedia(editMedia) });
  const overEdit = PLAIN(editBody) > POST_MAX;
  const canSave = (!emptyEdit || hasMedia(editMedia)) && !overEdit;
  const saveEdit = async () => {
    if (!canSave) return;
    if (overEdit) { toast.error("Posts can't exceed 3,000 characters."); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/chatter/posts/${p.id}`, {
        body: emptyEdit ? "" : editBody,
        media: hasMedia(editMedia) ? editMedia : null,
        post_type_ids: editPostTypeIds,
        industry_ids: editIndustryIds,
        link_preview_url: editLink.previewUrl,
      });
      setP(data);
      setEditing(false);
      toast.success("Update edited");
    } catch (e) { toast.error("Could not save changes"); }
    finally { setSaving(false); }
  };

  // Pinning exists only inside a Group, and only for an Owner/Admin of THAT Group.
  const canPin = Boolean(groupId && groupCanManage);

  return (
    <div ref={cardRef} id={`post-${p.id}`} className="py-5" data-testid={`chatter-post-${p.id}`}>
      {(p.kind === "rechat" || p.kind === "quote_rechat") && (
        <div className="mb-2 flex items-center gap-1.5 text-xs font-[540] text-muted-foreground" data-testid={`rechat-indicator-${p.id}`}>
          <Repeat2 className="h-3.5 w-3.5" /> Re-Chatted
        </div>
      )}
      {/* Row 1: avatar + name/timestamp (left) · controls (right) */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <AuthorAvatar card={p.author}>
            <Avatar className="h-10 w-10">
              {p.author && p.author.headshot_url && <AvatarImage src={fileUrl(p.author.headshot_url)} alt="" />}
              <AvatarFallback className="bg-teal-50 text-sm text-teal-700">{(p.author && p.author.display_name ? p.author.display_name[0] : "?")}</AvatarFallback>
            </Avatar>
          </AuthorAvatar>
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-1.5">
              <AuthorLink card={p.author} className="block truncate text-sm font-[600] text-foreground" testId={`post-author-${p.id}`} />
              {/* GRP-DEC-131 — a reader must never mistake a Group byline for a person. */}
              {(p.posted_as_group || p.author?.is_group) && (
                <span className="shrink-0 rounded bg-teal-50 px-1.5 py-0.5 text-[10px] font-[640] uppercase tracking-wide text-teal-700"
                      data-testid={`post-group-badge-${p.id}`}>Group</span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {p.author && p.author.primary_affiliation ? <><AffiliationLine aff={p.author.primary_affiliation} /> · </> : null}
              {/* §13.1 — Group attribution travels with a distributed post. */}
              {p.group_context?.group_name && !p.author?.is_group ? (
                <>via{" "}
                  {p.group_context.group_handle ? (
                    <Link to={`/g/${p.group_context.group_handle}`} className="hover:underline"
                          data-testid={`post-via-group-${p.id}`}>{p.group_context.group_name}</Link>
                  ) : <span data-testid={`post-via-group-${p.id}`}>{p.group_context.group_name}</span>}
                  {p.group_context.stream_name ? ` · ${p.group_context.stream_name}` : ""} ·{" "}
                </>
              ) : null}
              {timeAgo(p.created_at)}{p.edited ? " · edited" : ""}
              {p.group_global ? (
                <span className="ml-1 text-teal-700" data-testid={`post-global-badge-${p.id}`}>
                  · shared globally
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {p.can_edit && !editing && (
          <>
            {p.assist_editable && (
              <button onClick={() => setAssistEditOpen(true)} className="text-teal-700 hover:text-teal-900"
                aria-label="Edit Otter Assist" title="Edit Otter Assist"
                data-testid={`otter-assist-edit-settings-${p.id}`}><Sparkles className="h-4 w-4" /></button>
            )}
            <button onClick={startEdit} className="text-muted-foreground hover:text-primary" aria-label="Edit post" data-testid={`chatter-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="text-muted-foreground hover:text-destructive" aria-label="Delete post" data-testid={`chatter-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Delete this update?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
          )}
          {/* The options menu is NOT the author's-only menu: an organizer pins their own
              announcement far more often than somebody else's, so the menu renders whenever
              it has at least one action — Pin for an organizer, Report for a non-author. */}
          {!editing && (canPin || !p.can_edit) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-muted-foreground hover:text-foreground" aria-label="Post options" data-testid={`chatter-menu-${p.id}`}><MoreHorizontal className="h-4 w-4" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {/* Batch C.1 — Owner/Admin only. The server re-checks the role, so this is
                  convenience, not the control. */}
              {canPin ? (
                <DropdownMenuItem data-testid={`group-pin-${p.id}`}
                  onClick={async () => {
                    const verb = p.pinned ? "unpin" : "pin";
                    try {
                      await api.post(`/groups/${groupId}/posts/${p.id}/${verb}`, {});
                      setP((prev) => ({ ...prev, pinned: !p.pinned }));
                      toast.success(p.pinned ? "Unpinned from group" : "Pinned to group");
                      if (onPinChanged) onPinChanged();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Could not change the pin");
                    }
                  }}>
                  <Pin className="mr-2 h-4 w-4" />
                  {p.pinned ? "Unpin from Group" : "Pin to Group"}
                </DropdownMenuItem>
              ) : null}
              {canPin && !p.can_edit ? <DropdownMenuSeparator /> : null}
              {/* Phase 7 — Group Moderation. An organizer may REMOVE another member's post
                  from this Group; they never gain the author's edit. The server re-verifies
                  the role, the Group id and the post's own scope, so this is convenience. */}
              {p.can_group_moderate && groupId && !p.can_edit ? (
                <DropdownMenuItem className="text-destructive focus:text-destructive"
                  data-testid={`group-remove-post-${p.id}`}
                  onClick={async () => {
                    try {
                      await api.post(`/groups/${groupId}/moderation/posts/${p.id}/remove`, { reason: "" });
                      toast.success("Removed from the group");
                      if (onDeleted) onDeleted(p.id);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Could not remove this post");
                    }
                  }}>
                  <ShieldAlert className="mr-2 h-4 w-4" /> Remove from group
                </DropdownMenuItem>
              ) : null}
              {!p.can_edit ? (
                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setReportOpen(true)} data-testid={`chatter-report-${p.id}`}>
                  <Flag className="mr-2 h-4 w-4" /> Report post
                </DropdownMenuItem>
              ) : null}
              {/* Platform User Blocking V1 — only for a PERSONAL-identity author: the author
                  card carries `user_id` ONLY for a personal identity, so a Business /
                  Capital / Group byline never offers Block. */}
              {!p.can_edit && p.author && p.author.user_id && !p.author.is_group ? (
                <BlockMenuItem
                  targetUserId={p.author.user_id}
                  displayName={p.author.display_name}
                  source="chatter_post"
                  testId={`chatter-block-${p.id}`}
                />
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </div>

      {/* Batch C.1 — an Announcement and a pin are labelled in WORDS, never by colour
          alone (accessibility), and both reuse the standard Badge. */}
      {(p.announcement || p.notified_everyone || p.pinned) && !editing ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {p.announcement ? (
            <Badge data-testid={`post-announcement-${p.id}`} className="gap-1">
              <Megaphone className="h-3 w-3" aria-hidden="true" />Announcement
            </Badge>
          ) : null}
          {/* GRP-DEC-332 — an authoritative, historical record that this post invoked
              `@everyone`. Independent of Announcement, and read from the post's state, not
              from its text. It says the mention was INVOKED; it claims nothing about
              per-member delivery and never names a recipient. */}
          {p.notified_everyone ? (
            <Badge variant="secondary" data-testid={`post-everyone-${p.id}`}
                   className="gap-1" title="Sent with an @everyone mention">
              <AtSign className="h-3 w-3" aria-hidden="true" />everyone
            </Badge>
          ) : null}
          {p.pinned ? (
            <Badge variant="outline" data-testid={`post-pinned-${p.id}`} className="gap-1">
              <Pin className="h-3 w-3" aria-hidden="true" />Pinned
            </Badge>
          ) : null}
        </div>
      ) : null}

      {/* Row 2: post types on their own full-width row */}
      {!editing && p.post_types && p.post_types.length > 0 && (
        <div className="mt-2.5"><PostTypeChips items={p.post_types} /></div>
      )}

          {editing ? (
            <div className="mt-3" data-testid={`chatter-edit-form-${p.id}`}>
              <RichTextEditor value={editBody} onChange={setEditBody} placeholder="Share an update…" testId={`chatter-edit-input-${p.id}`} mentions emoji
                              onSubmit={() => { if (!saving && canSave) saveEdit(); }}
                              knownHandles={(p.mentions || []).map((m) => m.handle_norm || m.handle)} />
              {p.assist_editable && (
                <p className="mt-2 rounded-md bg-teal-50/70 px-2.5 py-2 text-[11px] text-teal-900" data-testid={`otter-assist-edit-notice-${p.id}`}>
                  This update has Otter Assist attached. If you change what you're asking for, Otter
                  Assist may look for matches again — it will never use another Assist. To change who
                  it looks for without touching your words, use Edit Otter Assist instead.
                </p>
              )}
              <ChatterComposerMedia value={editMedia} onChange={setEditMedia} />
              {!hasMedia(editMedia) && (
                <LinkPreviewCard preview={editLink.preview} onRemove={editLink.remove}
                                 testId={`edit-link-preview-${p.id}`} />
              )}
              <LabelSelectors
                postTypeOpts={postTypeOpts} industryOpts={industryOpts}
                postTypeIds={editPostTypeIds} setPostTypeIds={setEditPostTypeIds}
                industryIds={editIndustryIds} setIndustryIds={setEditIndustryIds}
                idSuffix={`-edit-${p.id}`}
              />
              <div className="mt-3 flex items-center justify-between gap-2">
                <span className={`text-xs tabular-nums ${overEdit ? "font-[600] text-destructive" : "text-muted-foreground"}`} data-testid={`chatter-edit-count-${p.id}`}>{PLAIN(editBody).toLocaleString()}/3,000</span>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={saving} data-testid={`chatter-edit-cancel-${p.id}`}><X className="mr-1.5 h-4 w-4" /> Cancel</Button>
                  <Button size="sm" onClick={saveEdit} disabled={saving || !canSave} data-testid={`chatter-edit-save-${p.id}`}>{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save</Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {p.body && p.body !== "<p></p>" && (
                <ClampedBody testId={`post-body-${p.id}`} expanded={!!autoOpen}>
                  <div className="mt-2"><RichTextView html={p.body} mentions={p.mentions} className="leading-[1.35]" /></div>
                </ClampedBody>
              )}
              {/* Outside the clamp on purpose: cards and media always show in full. */}
              <LinkPreviewCard preview={p.link_preview} testId={`post-link-preview-${p.id}`} />
              <PostMediaView media={p.media} />
              <IndustryChips items={p.industries} />
              <OtterAssistPostPill post={p} />
              {(p.kind === "rechat" || p.kind === "quote_rechat") && <RechatEmbed original={p.original} />}

              <div className="mt-3 flex flex-wrap items-center gap-0.5 sm:gap-2">
                <ReactionBar reactions={p.reactions} myReaction={p.my_reaction} onReact={react} testId={`post-react-${p.id}`}
                  target={reactionTarget} onSeeReactors={setReactorEmoji} />
                <ReactionCountButton reactions={p.reactions} target={reactionTarget}
                  testId={`post-reaction-count-${p.id}`} openEmoji={reactorEmoji} onOpenChange={setReactorEmoji} />
                <Button variant="ghost" size="sm" className={ACTION_BTN}
                        onClick={() => setShowComments((s) => !s)}
                        data-testid={`comments-toggle-${p.id}`}>
                  <MessageCircle className={ACTION_ICON} />
                  {commentCount > 0 ? `${commentCount} ` : ""}Comment{commentCount === 1 ? "" : "s"}
                </Button>
                {rechatVisible && identities.length > 0 && (
                  <RechatButton
                    p={p} setP={setP} identities={identities} defaultIdentityKey={defaultIdentityKey}
                    groupId={groupId} streamId={streamId}
                    postTypeOpts={postTypeOpts} industryOpts={industryOpts}
                    onRechatPosted={onRechatPosted} onRechatRemoved={onRechatRemoved}
                  />
                )}
                {/* Groups Phase 5 (§13.3) — ONLY the author sees this, and it flips the
                    SAME underlying post between group-only and globally distributed. It
                    belongs on the action row with React · Comment · Re-Chat because it is
                    an action ON the post, not a byline detail. */}
                {p.can_share_globally && !editing ? (
                  <Button size="sm" variant="ghost"
                          className={cn(ACTION_BTN, p.group_global && "text-teal-700")}
                          aria-pressed={Boolean(p.group_global)}
                          data-testid={`post-share-global-${p.id}`}
                          onClick={async () => {
                            try {
                              const { data } = await api.post(
                                `/groups/${p.group_context.group_id}/posts/${p.id}/global`,
                                { enabled: !p.group_global });
                              setP((prev) => ({ ...prev, group_global: data.group_global }));
                              toast.success(data.group_global
                                ? "Shared to global Chatter."
                                : "Removed from global Chatter — it stays in the group.");
                            } catch (e) {
                              toast.error(e?.response?.data?.detail
                                || "Could not change sharing");
                            }
                          }}>
                    <Globe className={ACTION_ICON} />
                    {p.group_global ? "Shared globally" : "Share globally"}
                  </Button>
                ) : null}
                {/* Group Member Messaging V1 — message the AUTHOR privately from the same
                    action row (React · Comment · Re-Chat · Message). It opens or resolves
                    the ONE canonical 1:1 Group conversation with that member; it is absent
                    on your own post, on a post published as the Group identity, and
                    wherever the Group has Member Messages turned off. */}
                {onMessageAuthor && p.author?.user_id && !p.posted_as_group
                  && !p.author?.is_group && p.author.user_id !== viewerUserId ? (
                  <Button variant="ghost" size="sm" className={ACTION_BTN}
                          onClick={() => onMessageAuthor(p.author)}
                          data-testid={`post-message-author-${p.id}`}>
                    <MessageSquare className={ACTION_ICON} /> Message
                  </Button>
                ) : null}
              </div>

              {showComments && <Comments postId={p.id} targetComment={targetComment} commentIdentity={commentIdentity} onCountChange={(d) => setCommentCount((c) => Math.max(0, c + d))} />}
            </>
          )}
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} contentType="post" contentId={p.id} onReported={() => onDeleted && onDeleted(p.id)} />
      {assistEditOpen && (
        <OtterAssistEditDialog assistId={p.assist_id}
          industryOpts={industryOpts} identityType={p.profile_type}
          onClose={() => setAssistEditOpen(false)} />
      )}
    </div>
  );
}

/**
 * The ONE Chatter feed component. Groups Batch C / Phase 4 reuses it rather than forking a
 * second posting/comment/reaction engine: `feedUrl` / `feedParams` point the read at the
 * in-group feed, `postPayload` carries the server-validated group context
 * (`group_id` / `stream_id`) into the SAME create endpoint, and `disableAssist` keeps a
 * group-scoped body out of Otter Assist. Every existing caller passes none of them and
 * behaves exactly as before.
 */
export function ChatterFeed({ profileType, profileId, ownerName, feedUrl, feedParams,
                              postPayload, disableAssist = false, emptyHint,
                              groupCanManage = false, onPinChanged, groupMemberCount = null,
                              focusPostId = null,
                              identityPicker = null, commentIdentity = null,
                              conversation = false, composerPlaceholder = null,
                              onMessageAuthor = null, viewerUserId = null,
                              heading = "Chatter" }) {
  // Groups Phase 5: `identityPicker` = {options, value, onChange, postAs:{type,id}} lets a
  // Group Owner/Admin publish as the Group through the SAME composer and the SAME create
  // endpoint — no second composer, no second posting path.
  const [meta, setMeta] = useState(null); // {is_owner, can_post}
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [body, setBody] = useState("");
  const [media, setMedia] = useState(EMPTY_MEDIA);
  const [postTypeIds, setPostTypeIds] = useState([]);
  const [industryIds, setIndustryIds] = useState([]);
  const [labelOpts, setLabelOpts] = useState({ postTypes: [], industries: [] });
  const [identities, setIdentities] = useState([]);
  const [rechatVisible, setRechatVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  // Groups Streams (GRP-DEC-320/321): `conversation` is a PRESENTATION mode over this one
  // engine — the composer moves below the feed, the feed reads oldest → newest, and the
  // less-used controls sit behind "More". No second posting path, no second feed query,
  // and Global Chatter (which never passes the prop) is untouched.
  const [more, setMore] = useState(false);
  // Stream composer disclosure on phones (desktop ignores this and stays expanded).
  const [composerOpen, setComposerOpen] = useState(false);
  const { user } = useAuth();
  const myInitials = `${(user?.first_name || "U")[0]}${(user?.last_name || "")[0] || ""}`
    .toUpperCase();
  const bottomRef = useRef(null);
  // Correction §§7–10: a Group/Stream NEVER auto-jumps to the newest message on load.
  // `atBottom` is derived from an IntersectionObserver on the bottom sentinel (one
  // observer, no scroll polling — correction §21); `newCount` counts content that
  // arrived while the reader was away from the bottom, and is what the explicit
  // "Jump to Latest" control clears.
  const [atBottom, setAtBottom] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const atBottomRef = useRef(true);
  const keepScrollRef = useRef(null);
  // The composer is sticky-pinned to the bottom of the viewport, so "the bottom of the
  // conversation" is the composer's TOP edge, not the viewport's. Measuring the composer
  // (it grows when "More" is open, or with a long draft) gives both the gap Jump to Latest
  // has to leave and the slack the sentinel observer needs, so the newest message is
  // never left underneath it.
  const composerRef = useRef(null);
  const [composerH, setComposerH] = useState(0);
  // Batch C.1 — Owner/Admin-only composer switches. Offered only to organizers; the
  // server independently verifies the human actor's role and 403s a member.
  const [announcement, setAnnouncement] = useState(false);
  const [notifyEveryone, setNotifyEveryone] = useState(false);
  // `@everyone` is confirmed before it is sent: it is the one composer action whose cost
  // lands on other people's notifications rather than on the author's own feed.
  const [confirmEveryone, setConfirmEveryone] = useState(false);
  const [params] = useSearchParams();
  const targetPost = params.get("post");
  const targetComment = params.get("comment");
  const LIMIT = 20;
  // GRP-DEC-334 — deep-link mode. `focusPostId` asks the server for a bounded WINDOW
  // around one post instead of paging down from the newest; from then on the feed reads by
  // `created_at` cursor in both directions, so reaching an old announcement never loads
  // the whole Stream into the browser.
  const [cursorMode, setCursorMode] = useState(Boolean(focusPostId));
  const [hasNewer, setHasNewer] = useState(false);

  const flashPost = useCallback((id) => {
    let tries = 0;
    const go = () => {
      const el = document.getElementById(`post-${id}`);
      if (el) {
        // `start` + the card's own `scroll-margin-top` keeps a deep-linked post clear of
        // the sticky Group tab bar and the sticky pinned strip above it.
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("so-flash");
        setTimeout(() => el.classList.remove("so-flash"), 1600);
        return;
      }
      if (tries++ < 15) setTimeout(go, 120);
    };
    setTimeout(go, 120);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(feedUrl || `/chatter/${profileType}/${profileId}`,
          { params: { page: 1, limit: LIMIT, ...(feedParams || {}),
            ...(focusPostId ? { focus_post_id: focusPostId } : {}) } });
        if (!active) return;
        setMeta({ is_owner: data.is_owner, can_post: data.can_post });
        setPosts(data.posts || []);
        setHasMore(!!data.has_more);
        setHasNewer(!!data.has_newer);
        setCursorMode(Boolean(focusPostId));
        setPage(1);
        setNewCount(0);
        if (focusPostId) flashPost(focusPostId);
      } catch (e) { if (active) { setMeta({ is_owner: false, can_post: false }); setPosts([]); setHasMore(false); } }
    })();
    return () => { active = false; };
  }, [profileType, profileId, feedUrl, focusPostId, JSON.stringify(feedParams || {})]);    // eslint-disable-line

  const loadMore = async () => {
    setLoadingMore(true);
    // Older history PREPENDS visually in conversation mode, so remember how far the
    // document stretched and give the reader their position back (correction §11).
    if (conversation) {
      keepScrollRef.current = document.documentElement.scrollHeight
        - window.scrollY;
    }
    try {
      const oldest = posts.length ? posts[posts.length - 1].created_at : null;
      const { data } = await api.get(feedUrl || `/chatter/${profileType}/${profileId}`,
        { params: cursorMode && oldest
          ? { before: oldest, limit: LIMIT, ...(feedParams || {}) }
          : { page: page + 1, limit: LIMIT, ...(feedParams || {}) } });
      setPosts((prev) => [...prev, ...(data.posts || [])]);
      setHasMore(!!data.has_more);
      if (!cursorMode) setPage((pp) => pp + 1);
    } catch (e) { toast.error("Could not load more updates"); }
    finally { setLoadingMore(false); }
  };

  // The other direction, which only a deep link can create: newer messages exist ABOVE
  // what the window returned, so they prepend to the newest-first array.
  const loadNewer = async () => {
    setLoadingMore(true);
    try {
      const newest = posts.length ? posts[0].created_at : null;
      const { data } = await api.get(feedUrl || `/chatter/${profileType}/${profileId}`,
        { params: { after: newest, limit: LIMIT, ...(feedParams || {}) } });
      setPosts((prev) => [...(data.posts || []), ...prev]);
      setHasNewer(!!data.has_newer);
    } catch (e) { toast.error("Could not load newer messages"); }
    finally { setLoadingMore(false); }
  };

  // Restore the reading position after an older page has been rendered above.
  useEffect(() => {
    if (keepScrollRef.current == null) return;
    const keep = keepScrollRef.current;
    keepScrollRef.current = null;
    window.scrollTo({
      top: document.documentElement.scrollHeight - keep, behavior: "instant",
    });
  }, [posts.length]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [pt, ind] = await Promise.all([
          api.get("/chatter/post-types"),
          api.get("/reference/industries"),
        ]);
        if (!active) return;
        setLabelOpts({
          postTypes: (pt.data || []).map((t) => ({ value: t.id, label: t.name, color: t.color })),
          industries: (ind.data || []).map((i) => ({ value: i.value, label: i.label })),
        });
      } catch (e) { /* labels are optional — ignore load errors */ }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [idn, cfg] = await Promise.all([
          api.get("/chatter/identities"),
          api.get("/chatter/rechat/config"),
        ]);
        if (!active) return;
        setIdentities(idn.data || []);
        setRechatVisible(!!(cfg.data && cfg.data.visible));
      } catch (e) { /* Re-Chat is optional — ignore load errors */ }
    })();
    return () => { active = false; };
  }, []);

  const defaultIdentityKey = useMemo(() => {
    if (!identities.length) return null;
    const own = `${profileType}:${profileId}`;
    if (identities.some((i) => identityKeyOf(i) === own)) return own;
    let last = null;
    try { last = localStorage.getItem(LAST_IDENTITY_KEY); } catch (e) { /* ignore */ }
    if (last && identities.some((i) => identityKeyOf(i) === last)) return last;
    return identityKeyOf(identities[0]);
  }, [identities, profileType, profileId]);

  // A new post that belongs in THIS feed is inserted straight away. In a Group the feed
  // is keyed by group + stream rather than by a profile, which is why the old
  // profile-only test silently dropped every in-group Re-Chat (correction §5). The group
  // context travels on `group_context`, the same shape the in-group feed returns.
  const belongsHere = (np) => {
    if (!np) return false;
    const gid = postPayload?.group_id;
    if (gid) {
      const ctx = np.group_context || {};
      if ((ctx.group_id || np.group_id) !== gid) return false;
      const sid = (feedParams || {}).stream_id;
      return !sid || (ctx.stream_id || np.stream_id) === sid;
    }
    return np.profile_type === profileType && np.profile_id === profileId;
  };

  const addPost = (np) => {
    setPosts((prev) => (prev.some((x) => x.id === np.id) ? prev : [np, ...prev]));
    // Reading history? Surface it instead of yanking the viewport (correction §9).
    if (conversation && !atBottomRef.current) setNewCount((c) => c + 1);
  };

  const onRechatPosted = (np) => { if (belongsHere(np)) addPost(np); };
  const onRechatRemoved = (removedId) =>
    setPosts((prev) => prev.filter((x) => x.id !== removedId));

  const assist = useOtterAssist({
    identityType: profileType, identityId: profileId, postTypeIds,
    postDraft: useMemo(() => ({ body, media, post_type_ids: postTypeIds, industry_ids: industryIds }),
      [body, media, postTypeIds, industryIds]),
    onRestoreDraft: useCallback((d) => {
      if (d.body) setBody(d.body);
      if (d.media) setMedia(d.media);
      if (d.post_type_ids) setPostTypeIds(d.post_type_ids);
      if (d.industry_ids) setIndustryIds(d.industry_ids);
    }, []),
  });
  const [assistFlow, setAssistFlow] = useState(null);

  const emptyBody = !body || body === "<p></p>";
  const link = useLinkPreview(body, { enabled: !hasMedia(media) });
  const overPost = PLAIN(body) > POST_MAX;
  const canPost = (!emptyBody || hasMedia(media)) && !overPost;

  const post = async () => {
    if (!canPost) return;
    if (overPost) { toast.error("Posts can't exceed 3,000 characters."); return; }
    setBusy(true);
    try {
      const payload = {
        body: emptyBody ? "" : body, media: hasMedia(media) ? media : null,
        post_type_ids: postTypeIds, industry_ids: industryIds,
        link_preview_url: link.previewUrl,
      };
      // Otter Assist (OAD-051): ONE request. Absent key => untouched Chatter payload.
      if (assist.payload && !disableAssist) payload.otter_assist = assist.payload;
      if (postPayload) Object.assign(payload, postPayload);
      if (postPayload?.group_id && groupCanManage) {
        if (announcement) payload.announcement = true;
        if (notifyEveryone) payload.notify_everyone = true;
      }
      const setupSnapshot = assist.setup;
      const target = identityPicker?.postAs || { type: profileType, id: profileId };
      const { data: created } = await api.post(`/chatter/${target.type}/${target.id}`, payload);
      setPosts((prev) => [created, ...prev]);
      setBody("");
      setMedia(EMPTY_MEDIA);
      link.reset();
      setPostTypeIds([]);
      setIndustryIds([]);
      assist.reset();
      if (created?.otter_assist?.attached) {
        setAssistFlow({ info: created.otter_assist, setup: setupSnapshot });
        toast.success("Update posted — Otter Assist is finding matches");
      } else if (created?.otter_assist && !created.otter_assist.attached) {
        toast.error("Your update was posted, but Otter Assist couldn't start. Please try again.");
      } else {
        toast.success(conversation ? "Sent" : "Update posted");
      }
      setAnnouncement(false);
      setNotifyEveryone(false);
      // On a phone the Stream composer folds back to its bar after posting so the feed
      // fills the screen again. Desktop is unaffected: the expanded composer there is
      // shown by CSS from `lg` up regardless of this flag.
      setComposerOpen(false);
      if (conversation) {
        setNewCount(0);
        setTimeout(() => bottomRef.current?.scrollIntoView(
          { behavior: "smooth", block: "end" }), 50);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post"); }
    finally { setBusy(false); }
  };

  const onDeleted = (id) => setPosts((prev) => prev.filter((x) => x.id !== id));

  // ONE IntersectionObserver on the bottom sentinel tells us whether the reader is at
  // the newest content. Nothing scrolls on mount: a Group opens at the normal top of the
  // page (correction §7) and "Jump to Latest" is the explicit way down (correction §8).
  // The root is shrunk by the composer's height (less a 32px tolerance, so a sentinel
  // sitting exactly on the composer's top edge still reads as "seen") — a message hidden
  // behind the composer does NOT count as read.
  useEffect(() => {
    if (!conversation) return undefined;
    const el = bottomRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([entry]) => {
      atBottomRef.current = entry.isIntersecting;
      setAtBottom(entry.isIntersecting);
      if (entry.isIntersecting) setNewCount(0);
    }, { rootMargin: `0px 0px -${Math.max(0, composerH - 32)}px 0px` });
    io.observe(el);
    return () => io.disconnect();
  }, [conversation, meta, composerH]);

  // Track the sticky composer's height without a scroll or resize handler per message.
  useEffect(() => {
    const el = composerRef.current;
    if (!conversation || !el) { setComposerH(0); return undefined; }
    setComposerH(el.offsetHeight);
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setComposerH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [conversation, meta, more]);

  const jumpToLatest = () => {
    setNewCount(0);
    // After a deep link there may be newer messages the window never fetched, so "latest"
    // means re-reading the newest page rather than scrolling to the bottom of a window.
    if (cursorMode && hasNewer) {
      (async () => {
        try {
          const { data } = await api.get(feedUrl || `/chatter/${profileType}/${profileId}`,
            { params: { page: 1, limit: LIMIT, ...(feedParams || {}) } });
          setPosts(data.posts || []);
          setHasMore(!!data.has_more);
          setHasNewer(false);
          setCursorMode(false);
          setPage(1);
          setTimeout(() => bottomRef.current?.scrollIntoView(
            { behavior: "smooth", block: "end" }), 60);
        } catch (e) { toast.error("Could not load the latest messages"); }
      })();
      return;
    }
    // The newest page is always the one loaded first, so the newest message is already
    // in the DOM — no pagination round-trip, and never "load everything" (correction §11).
    // `scroll-margin-bottom` on the sentinel is what keeps the newest message clear of
    // the composer rather than tucked behind it.
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  if (!meta) return null;
  const { is_owner, can_post } = meta;
  // Whoever may post AS this profile sees the composer (owner/admin/contributor on a
  // published org, or their own personal profile). Falls back to is_owner for safety.
  const canPostAs = can_post ?? is_owner;
  // Conversational chronology: the API still pages newest-first (so page 2 is older
  // history); a Stream simply renders that array bottom-up, which keeps the newest message
  // at the bottom and puts "Load earlier" at the top where a chat expects it.
  const ordered = conversation ? [...posts].reverse() : posts;

  const identityRow = identityPicker && identityPicker.options.length > 1 ? (
    <div className={conversation ? "flex items-center gap-2" : "mb-2 flex items-center gap-2"}>
      <span className="text-xs text-muted-foreground">Post as</span>
      <Select value={identityPicker.value} onValueChange={identityPicker.onChange}>
        <SelectTrigger className="h-8 w-auto min-w-[10rem] text-xs" data-testid="chatter-post-as">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {identityPicker.options.map((i) => (
            <SelectItem key={identityKeyOf(i)} value={identityKeyOf(i)}
                        data-testid={`chatter-post-as-option-${i.identity_id}`}>
              {i.display_name}{i.identity_type === "group" ? " (group)" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  ) : null;

  const linkPreviewRow = !hasMedia(media) ? (
    <>
      {link.loading && !link.preview && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="link-preview-loading">
          <Loader2 className="h-3 w-3 animate-spin" /> Fetching link preview…
        </p>
      )}
      <LinkPreviewCard preview={link.preview} onRemove={link.remove} testId="composer-link-preview" />
    </>
  ) : null;

  const sendButton = (
    <Button onClick={() => {
              // Announcements post straight through — classification costs nobody a
              // notification. `@everyone` always asks first.
              if (notifyEveryone && postPayload?.group_id) setConfirmEveryone(true);
              else post();
            }} disabled={busy || !canPost} data-testid="chatter-post-button"
            size={conversation ? "sm" : "default"}>
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        : (conversation ? <Send className="mr-1.5 h-4 w-4" /> : null)}
      {assist.payload ? "Post with Otter Assist" : (conversation ? "Send" : "Post update")}
    </Button>
  );

  // Ctrl+Enter / Cmd+Enter sends from the editor itself. It routes through the SAME
  // guards as the button — nothing posts while busy, over the limit or empty, and
  // `@everyone` still has to be confirmed in the dialog.
  const submitShortcut = () => {
    if (busy || !canPost) return;
    if (notifyEveryone && postPayload?.group_id) setConfirmEveryone(true);
    else post();
  };
  const shortcutHint = (
    <span className="hidden text-[11px] text-muted-foreground sm:inline"
          data-testid="composer-shortcut-hint">
      {conversation ? "Ctrl+Enter to send" : "Ctrl+Enter to post"}
    </span>
  );

  // The recipient number comes from the SERVER's own active-member count — the client
  // never recomputes eligibility, and "up to" is literal: the author is never notified
  // and a member who has muted the Group is dropped server-side.
  const everyoneDialog = (
    <AlertDialog open={confirmEveryone} onOpenChange={setConfirmEveryone}>
      <AlertDialogContent data-testid="everyone-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Notify everyone in this group?</AlertDialogTitle>
          <AlertDialogDescription>
            {typeof groupMemberCount === "number" && groupMemberCount > 0
              ? `This will notify up to ${groupMemberCount.toLocaleString()} member${groupMemberCount === 1 ? "" : "s"} — everyone active in this group except you. `
              : "This will notify every active member of this group except you. "}
            @everyone is rate limited, so keep it for things that genuinely can't wait.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="everyone-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={post} data-testid="everyone-confirm-send">
            Notify everyone
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // ── COMPACT (Stream) composer ────────────────────────────────────────────────
  // A message box, not a social-post card: text input + Send, with Photo/Video, Post type
  // and Industry on ONE compact row and everything else (link preview, Otter Assist, the
  // character counter) behind "More". Nothing was removed — it is disclosure, not loss.
  const compactComposer = (
    <div className="space-y-2" data-testid="stream-composer">
      {identityRow}
      <div className="rounded-xl border border-border bg-card p-2 shadow-sm">
        <RichTextEditor value={body} onChange={setBody}
                        placeholder={composerPlaceholder || "Message this stream…"}
                        testId="chatter-composer" mentions emoji
                        onSubmit={submitShortcut}
                        editorClassName="min-h-[2.75rem]" />
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <ChatterComposerMedia value={media} onChange={setMedia} compact />
          <LabelSelectors
            postTypeOpts={labelOpts.postTypes} industryOpts={labelOpts.industries}
            postTypeIds={postTypeIds} setPostTypeIds={setPostTypeIds}
            industryIds={industryIds} setIndustryIds={setIndustryIds}
            idSuffix="-composer" compact
          />
          {postPayload?.group_id && groupCanManage ? (
            <>
              <Button type="button" variant={announcement ? "default" : "ghost"} size="sm"
                      className="h-8 px-2 text-xs" aria-pressed={announcement}
                      onClick={() => setAnnouncement((v) => !v)}
                      data-testid="composer-announcement-toggle">
                <Megaphone className="mr-1 h-3.5 w-3.5" aria-hidden="true" />Announcement
              </Button>
              <Button type="button" variant={notifyEveryone ? "default" : "ghost"} size="sm"
                      className="h-8 px-2 text-xs" aria-pressed={notifyEveryone}
                      onClick={() => setNotifyEveryone((v) => !v)}
                      data-testid="composer-everyone-toggle">
                Notify @everyone
              </Button>
            </>
          ) : null}
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs"
                  aria-expanded={more} aria-controls="stream-composer-more"
                  onClick={() => setMore((v) => !v)} data-testid="stream-composer-more">
            {more ? <ChevronUp className="mr-1 h-3.5 w-3.5" />
              : <ChevronDown className="mr-1 h-3.5 w-3.5" />}More
          </Button>
          <span className="ml-auto flex items-center gap-2">
            {overPost ? (
              <span className="text-xs font-[600] text-destructive" data-testid="chatter-composer-count">
                {PLAIN(body).toLocaleString()}/3,000
              </span>
            ) : null}
            {shortcutHint}
            {sendButton}
          </span>
        </div>
        {more ? (
          <div className="mt-2 space-y-2 border-t border-border pt-2" id="stream-composer-more">
            {linkPreviewRow}
            <OtterAssistComposerPanel assist={assist} body={body}
                                      industryOpts={labelOpts.industries} />
            <span className={`text-xs tabular-nums ${overPost ? "font-[600] text-destructive" : "text-muted-foreground"}`}
                  data-testid="chatter-composer-count-full">
              {PLAIN(body).toLocaleString()}/3,000
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );

  const fullComposer = (
    <div className="mt-4">
      {identityRow}
      <RichTextEditor value={body} onChange={setBody} placeholder="Share an update with your network…" testId="chatter-composer" mentions emoji onSubmit={submitShortcut} />
      {linkPreviewRow}
      <ChatterComposerMedia value={media} onChange={setMedia} />
      <LabelSelectors
        postTypeOpts={labelOpts.postTypes} industryOpts={labelOpts.industries}
        postTypeIds={postTypeIds} setPostTypeIds={setPostTypeIds}
        industryIds={industryIds} setIndustryIds={setIndustryIds}
        idSuffix="-composer"
      />
      <div className="mt-3">
        <OtterAssistComposerPanel assist={assist} body={body} industryOpts={labelOpts.industries} />
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <span className={`text-xs tabular-nums ${overPost ? "font-[600] text-destructive" : "text-muted-foreground"}`} data-testid="chatter-composer-count">{PLAIN(body).toLocaleString()}/3,000</span>
        <span className="flex items-center gap-2">{shortcutHint}{sendButton}</span>
      </div>
    </div>
  );

  const loadMoreButton = hasMore ? (
    <div className={conversation ? "mb-3 flex justify-center" : "mt-4 flex justify-center"}>
      <Button variant="secondary" size={conversation ? "sm" : "default"} onClick={loadMore}
              disabled={loadingMore} data-testid="chatter-load-more">
        {loadingMore ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {conversation ? "Load earlier messages" : "Load more"}
      </Button>
    </div>
  ) : null;

  const loadNewerButton = hasNewer ? (
    <div className="mt-3 flex justify-center">
      <Button variant="secondary" size={conversation ? "sm" : "default"} onClick={loadNewer}
              disabled={loadingMore} data-testid="chatter-load-newer">
        {loadingMore ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          : <ArrowDown className="mr-1.5 h-4 w-4" aria-hidden="true" />}
        Load newer messages
      </Button>
    </div>
  ) : null;

  const feedBody = ordered.length === 0 ? (
    <div className="mt-4">
      <EmptyState
        icon={MessageCircle}
        title={conversation ? "No messages yet" : "No Chatter posts yet"}
        description={canPostAs
          ? (conversation ? (emptyHint || "Start the conversation — your message appears here.")
            : "Share your first update to keep your network in the loop.")
          : `${ownerName || "This profile"} hasn't posted any updates yet.`}
        testId="chatter-empty"
      />
    </div>
  ) : (
    <>
      {conversation ? loadMoreButton : null}
      <div className={`${conversation ? "" : "mt-4"} divide-y divide-border ${!conversation && canPostAs ? "border-t border-border pt-1" : ""}`}>
        {ordered.map((p) => <PostCard key={p.id} post={p} onDeleted={onDeleted} commentIdentity={commentIdentity} groupId={postPayload?.group_id} streamId={postPayload?.stream_id} autoOpen={targetPost === p.id} targetComment={targetPost === p.id ? targetComment : null} postTypeOpts={labelOpts.postTypes} industryOpts={labelOpts.industries} identities={identities} rechatVisible={rechatVisible} defaultIdentityKey={defaultIdentityKey} onRechatPosted={onRechatPosted} onRechatRemoved={onRechatRemoved} groupCanManage={groupCanManage} onPinChanged={onPinChanged} onMessageAuthor={onMessageAuthor} viewerUserId={viewerUserId} />)}
      </div>
      {conversation ? null : loadMoreButton}
      {loadNewerButton}
    </>
  );

  const assistDialog = assistFlow ? (
    <OtterAssistActivationDialog
      info={assistFlow.info} setup={assistFlow.setup} config={assist.config}
      industryOpts={labelOpts.industries} identityType={profileType}
      onClose={async () => {
        setAssistFlow(null);
        try {
          const { data } = await api.get(feedUrl || `/chatter/${profileType}/${profileId}`,
            { params: { page: 1, limit: LIMIT, ...(feedParams || {}) } });
          setPosts(data.posts || []); setHasMore(!!data.has_more); setPage(1);
        } catch (e) { /* the post is already in the list */ }
      }}
    />
  ) : null;

  if (conversation) {
    // Compact, keyboard-reachable and labelled in words, not just an arrow (§22). It is
    // anchored ABOVE the sticky composer, so it can never cover a message, the composer
    // or the mobile safe area (§8).
    const jumpButton = !atBottom && ordered.length > 0 ? (
      <Button size="sm" onClick={jumpToLatest} data-testid="chatter-jump-to-latest"
              aria-label={newCount > 0
                ? `Jump to latest — ${newCount} new message${newCount === 1 ? "" : "s"}`
                : "Jump to latest messages"}
              className="pointer-events-auto rounded-full shadow-lg">
        <ArrowDown className="mr-1.5 h-4 w-4" aria-hidden="true" />
        {newCount > 0 ? `${newCount} new message${newCount === 1 ? "" : "s"}`
          : "Jump to Latest"}
      </Button>
    ) : null;
    return (
      <div data-testid="chatter-feed" className="flex min-h-0 flex-col">
        {feedBody}
        <div ref={bottomRef} aria-hidden="true" className="h-1"
             style={{ scrollMarginBottom: `${composerH + 16}px` }} />
        {canPostAs ? (
          /* Sticky to the bottom of the viewport so the conversation scrolls above it.
             `pb-[env(safe-area-inset-bottom)]` keeps it clear of the mobile home bar, and
             it never covers a message because Jump to Latest leaves exactly its height
             as a gap (see `scrollMarginBottom` above). */
          <div ref={composerRef} className="sticky bottom-0 z-10 -mx-1 mt-3 bg-background/95 px-1 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 backdrop-blur supports-[backdrop-filter]:bg-background/80 relative">
            {jumpButton ? (
              <div className="pointer-events-none absolute -top-11 left-0 right-0 flex justify-center">
                {jumpButton}
              </div>
            ) : null}
            {/* Below `lg` the composer starts COLLAPSED as an avatar + bar so the
                conversation fills a phone screen; one tap opens the whole Stream
                composer. From `lg` up the expanded composer is always shown, so
                desktop behaviour is exactly as it was. */}
            {!composerOpen ? (
              <button type="button" onClick={() => setComposerOpen(true)}
                      className="flex w-full items-center gap-2 rounded-full border border-border bg-card px-3 py-2 text-left shadow-sm transition-colors hover:bg-secondary lg:hidden"
                      data-testid="stream-composer-collapsed">
                <Avatar className="h-7 w-7 shrink-0">
                  {user?.picture ? <AvatarImage src={fileUrl(user.picture)} alt="" /> : null}
                  <AvatarFallback className="bg-teal-50 text-[11px] text-teal-700">
                    {myInitials}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate text-sm text-muted-foreground">
                  {composerPlaceholder || "Message this stream…"}
                </span>
                <Pencil className="ml-auto h-4 w-4 shrink-0 text-muted-foreground"
                        aria-hidden="true" />
              </button>
            ) : null}
            <div className={composerOpen ? "" : "hidden lg:block"}>{compactComposer}</div>
          </div>
        ) : jumpButton ? (
          <div className="pointer-events-none sticky bottom-4 z-10 flex justify-center">
            {jumpButton}
          </div>
        ) : null}
        {assistDialog}
        {everyoneDialog}
      </div>
    );
  }

  return (
    <Card className="border-0 p-6" data-testid="chatter-feed">
      <h3 className="text-lg font-[640] text-primary">{heading}</h3>
      {canPostAs && fullComposer}
      {feedBody}
      {assistDialog}
      {everyoneDialog}
    </Card>
  );
}
